import React from 'react';
import cx from 'classnames';
import {
  DisclosureTreeNodeDataProps,
  DisclosureContentTreeNodeDataProps,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';

type DisclosureProps = {
  id: string;
  editorClassName?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  isEditor?: boolean;
} & DisclosureTreeNodeDataProps;

type DisclosureContentProps = {
  id: string;
  children?: React.ReactNode;
  editorClassName?: string;
  isDragging?: boolean;
  isEditor?: boolean;
} & DisclosureContentTreeNodeDataProps;

// TypeScript declarations for Tailwind Plus Elements
/* eslint-disable @typescript-eslint/no-namespace */
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'el-disclosure': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        hidden?: boolean;
        open?: boolean;
        id?: string;
        class?: string;
        key?: string;
      };
    }
  }
}
/* eslint-enable @typescript-eslint/no-namespace */

export const DisclosureContent = React.memo(
  React.forwardRef<HTMLDivElement, DisclosureContentProps>(
    ({ className, editorClassName, domTargetId, defaultOpen, children, isDragging = false, isEditor = false }, ref) => {
      const transformedClassName = transformToContainerQueries(className, { isEditor });

      // Use div when anything is being dragged to avoid el-disclosure observer issues
      if (isDragging) {
        return (
          <div ref={ref} hidden={!defaultOpen} id={domTargetId} className={cx(transformedClassName, editorClassName)}>
            {children}
          </div>
        );
      }

      // Use el-disclosure normally
      return (
        <el-disclosure
          ref={ref}
          hidden={!defaultOpen}
          id={domTargetId}
          class={cx(transformedClassName, editorClassName)}
        >
          {children}
        </el-disclosure>
      );
    },
  ),
);

DisclosureContent.displayName = 'DisclosureContent';

export const Disclosure = React.memo(
  React.forwardRef<HTMLDivElement, DisclosureProps>(
    ({ id, className, editorClassName, children, onClick, onDoubleClick, isEditor = false }, ref) => {
      return (
        <div
          ref={ref}
          id={id}
          className={cx(transformToContainerQueries(className, { isEditor }), editorClassName)}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
        >
          {children}
        </div>
      );
    },
  ),
);

Disclosure.displayName = 'Disclosure';
